import { AfterViewInit, ChangeDetectionStrategy, Component,
        EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { IDropDownMenuItem } from 'flux-core/src/ui';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CollabModel, CollaboratorType } from '../../../collab/model/collaborator.mdl';
import { AlterCollabDropDown } from '../alter-collab-dropdown.cmp';

@Component({
    selector: 'collab-list-item',
    templateUrl: './collab-list-item.cmp.html',
    styleUrls: [ './collab-list-item.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * This component is used to encapsulate functionality such as
 * displaying and removing a collaborator from the collaborator
 * list in the project / diagram share window. A collabortator
 * can be added by using the <collaborator-item></collaborator-item>
 * selector.
 */
export class CollabListItem implements AfterViewInit, OnDestroy, OnInit {

    /**
     * Input collaborator from the diagram collabs array
     */
    @Input()
    public collab: CollabModel;

    /**
     * Collaborator types which are not allowed to change.
     */
    @Input()
    public imutableCollabTypes: CollaboratorType[] = [ CollaboratorType.OWNER ];

    /**
     * DropDown component
     */
    @ViewChild( AlterCollabDropDown )
    public collabType: AlterCollabDropDown;

    @Output()
    public onRemove: EventEmitter<CollabModel> = new EventEmitter();

    @Output()
    public onRoleChange: EventEmitter<{ userId: string, role: CollaboratorType}> = new EventEmitter();

    /**
     * An observable which get triggered when delete button is get clicked
     */
    public remove: Subject<CollabModel>;

    /**
     * An observable which get triggered when user changes the role from CollabTypeDropdown
     */
    public roleChange: Subject<{ userId: string, role: number}>;

    /*
     * This holds the currently selected role in the collaborators drop down
     */
    public selectedRole: BehaviorSubject<CollaboratorType>;

    /**
     * The list of subscriptions held by this class
     */
    protected subscriptions: Subscription[] = [];

    constructor( public translate: TranslateService ) {
        this.remove = new Subject();
        this.roleChange = new Subject();
    }

    public ngOnInit() {
        this.selectedRole = new BehaviorSubject( this.collab.role );
    }

    /**
     * Listen for the select change event on drop down
     */
    public ngAfterViewInit() {
        if ( this.collabType ) {
            const selctSub = this.collabType.select.subscribe( item => this.handleDropdownSelect( item ));
            this.subscriptions.push( selctSub );
        }
    }

    /**
     * Destroys and clears all the resources used by the
     * colab list item.
     */
    public ngOnDestroy() {
        while ( this.subscriptions.length > 0 ) {
            this.subscriptions.pop().unsubscribe();
        }
    }

    /**
     * This checks the given collaborator can change the role.
     */
    public mutableCollabType(): boolean {
        return !this.imutableCollabTypes.includes( this.collab.role );
    }

    /**
     * Get Collaborator type translation.
     * @returns
     */
    public getCollabTypeName(): Observable<string> {
        return this.translate.get( `ROLES.${CollaboratorType[this.collab.role]}` );
    }

    /**
     * Handles the dropdown selections
     */
    public handleDropdownSelect( item: IDropDownMenuItem ) {
        if ( item.value === 'REMOVE' ) {
            this.onRemove.emit( this.collab );
            this.remove.next( this.collab );
        } else {
            this.roleChange.next({ userId: this.collab.id, role: item.value });
            this.selectedRole.next( item.value );
            this.onRoleChange.emit({ userId: this.collab.id, role: item.value });
        }
    }

}
